import ExcelJS from "exceljs";
import * as XLSX from "xlsx";

const HEADERS = [
  "DATE", "Voucher No (optional)", "Reference", "Party Ledger",
  "Item", "HSN/SAC", "Qty", "Unit", "Rate", "Disc %", "Amount",
  "CGST", "SGST", "IGST", "Total", "Voucher Type",
];

const VOUCHER_TYPES = ["Purchase", "Sales", "Debit Note", "Credit Note"];

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

/**
 * Build the bulk-purchase / bulk-inventory template.
 * - Multi-line vouchers: rows that share the same Voucher No + Date + Party get merged.
 * - Leave Voucher No blank to have each row become its own voucher.
 */
export async function buildBulkInventoryTemplate(ledgerNames, itemNames, companyName = "company") {
  const wb = new ExcelJS.Workbook();
  wb.creator = "AXIORA";
  wb.created = new Date();

  // Hidden ref sheets for big dropdown lists
  const ldgRef = wb.addWorksheet("_Ledgers", { state: "veryHidden" });
  ledgerNames.forEach((n, i) => { ldgRef.getCell(i + 1, 1).value = n; });
  const itmRef = wb.addWorksheet("_Items", { state: "veryHidden" });
  itemNames.forEach((n, i) => { itmRef.getCell(i + 1, 1).value = n; });

  const sh = wb.addWorksheet("Purchase Vouchers", { views: [{ state: "frozen", ySplit: 1 }] });
  sh.columns = HEADERS.map((h, i) => ({ header: h, width: [14, 18, 16, 24, 24, 12, 10, 8, 12, 10, 14, 10, 10, 10, 14, 14][i] }));

  // Header style
  const headerRow = sh.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF7C2D12" } }; // brown for purchase
  headerRow.alignment = { vertical: "middle", horizontal: "left" };
  headerRow.height = 22;
  headerRow.eachCell((c) => {
    c.protection = { locked: true };
    c.border = {
      top: { style: "thin", color: { argb: "FF431407" } },
      bottom: { style: "thin", color: { argb: "FF431407" } },
    };
  });

  const MAX = 500;
  const lastRow = MAX + 1;
  const ldgRangeRef = ledgerNames.length ? `=_Ledgers!$A$1:$A$${ledgerNames.length}` : '"(no ledgers)"';
  const itmRangeRef = itemNames.length ? `=_Items!$A$1:$A$${itemNames.length}` : '"(no items)"';
  const vtypeRef = `"${VOUCHER_TYPES.join(",")}"`;

  for (let r = 2; r <= lastRow; r++) {
    // A: Date
    sh.getCell(`A${r}`).numFmt = "dd/mm/yyyy";
    sh.getCell(`A${r}`).dataValidation = {
      type: "date", operator: "greaterThan",
      formulae: [new Date(1900, 0, 1)],
      allowBlank: true,
      showErrorMessage: true, errorTitle: "Invalid date",
      error: "Please enter date in dd/mm/yyyy format.",
    };
    // D: Party ledger dropdown
    sh.getCell(`D${r}`).dataValidation = {
      type: "list", allowBlank: true, formulae: [ldgRangeRef],
      showErrorMessage: true, errorTitle: "Invalid ledger",
      error: "Pick a ledger from the dropdown (or leave blank — auto-create option creates the supplier).",
    };
    // E: Item dropdown (allow blank — will be auto-created)
    sh.getCell(`E${r}`).dataValidation = {
      type: "list", allowBlank: true, formulae: [itmRangeRef],
      showErrorMessage: false,  // allow custom item names for auto-create
    };
    // Numeric cols G/I/J/K/L/M/N/O — qty, rate, disc%, amount, cgst, sgst, igst, total
    ["G", "I", "J", "K", "L", "M", "N", "O"].forEach((col) => {
      sh.getCell(`${col}${r}`).numFmt = "#,##0.00";
      sh.getCell(`${col}${r}`).dataValidation = {
        type: "decimal", operator: "greaterThanOrEqual",
        formulae: [0], allowBlank: true,
        showErrorMessage: true, errorTitle: "Invalid number",
        error: "Only numbers (≥ 0) are accepted in this column.",
      };
    });
    // P: Voucher Type dropdown (default Purchase)
    sh.getCell(`P${r}`).dataValidation = {
      type: "list", allowBlank: true, formulae: [vtypeRef],
      showErrorMessage: true, errorTitle: "Invalid voucher type",
      error: "Pick one of Purchase / Sales / Debit Note / Credit Note.",
    };
  }

  // Unlock A:O for rows 2+
  for (let r = 2; r <= lastRow; r++) {
    for (let c = 1; c <= HEADERS.length; c++) {
      sh.getCell(r, c).protection = { locked: false };
    }
  }

  // Two sample rows showing multi-line voucher pattern
  if (itemNames.length && ledgerNames.length) {
    const today = new Date();
    const sample = (r, vals) => {
      vals.forEach((v, i) => {
        const c = sh.getCell(r, i + 1);
        c.value = v;
        c.font = { italic: true, color: { argb: "FF94A3B8" } };
      });
    };
    // Two lines sharing voucher_no "BILL-001" → one voucher with two line items
    sample(2, [today, "BILL-001", "INV/2026/100", ledgerNames[0], itemNames[0], "8471", 10, "Nos", 1000, 0, 10000, 900, 900, 0, 11800, "Purchase"]);
    sample(3, [today, "BILL-001", "INV/2026/100", ledgerNames[0], itemNames[itemNames.length - 1] || itemNames[0], "8471", 5, "Nos", 500, 5, 2375, 213.75, 213.75, 0, 2802.5, "Purchase"]);
  }

  sh.properties.defaultRowHeight = 18;
  await sh.protect("", {
    selectLockedCells: false, selectUnlockedCells: true,
    formatCells: false, formatColumns: false, formatRows: false,
    insertRows: false, insertColumns: false,
    deleteRows: false, deleteColumns: false,
    sort: false, autoFilter: false, pivotTables: false,
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const safe = String(companyName).replace(/[^a-z0-9-]+/gi, "-").toLowerCase() || "company";
  triggerDownload(blob, `axiora-bulk-purchase-${safe}.xlsx`);
}

const excelSerialToISO = (n) => new Date(Math.round((n - 25569) * 86400 * 1000)).toISOString().slice(0, 10);

const parseDateCell = (v) => {
  if (v == null || v === "") return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "number") return excelSerialToISO(v);
  const s = String(v).trim().split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dmy) { const [, d, m, y] = dmy; return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`; }
  return s;
};

const num = (v) => typeof v === "number" ? v : (Number(String(v ?? "").replace(/,/g, "")) || 0);

const SAMPLE_REFS = new Set(["INV/2026/100"]);

export async function parseBulkInventoryFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const sheet = wb.Sheets["Purchase Vouchers"] || wb.Sheets[wb.SheetNames[0]];
  if (!sheet) throw new Error("No sheet found in the uploaded file");
  const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
  const rows = [];
  for (let i = 1; i < aoa.length; i++) {
    const r = aoa[i];
    if (!r || r.every((c) => c === "" || c == null)) continue;
    const [date, vno, ref, party, item, hsn, qty, unit, rate, discPct, amount, cgst, sgst, igst, total, vtype] = r;
    // Skip sample rows
    if (SAMPLE_REFS.has(String(ref || "").trim())) continue;
    rows.push({
      excel_row: i + 1,
      date: parseDateCell(date),
      voucher_no: String(vno || "").trim(),
      reference: String(ref || "").trim(),
      party_ledger: String(party || "").trim(),
      item_name: String(item || "").trim(),
      hsn: String(hsn || "").trim(),
      qty: num(qty),
      unit: String(unit || "").trim() || "Nos",
      rate: num(rate),
      discount_pct: num(discPct),
      amount: num(amount),
      cgst: num(cgst), sgst: num(sgst), igst: num(igst),
      total: num(total),
      voucher_type: String(vtype || "").trim() || "Purchase",
    });
  }
  return rows;
}

export function validateInventoryRowsClient(rows, ledgerNames, itemNames, autoCreate) {
  const ldg = new Set(ledgerNames.map((n) => n.toLowerCase()));
  const itm = new Set(itemNames.map((n) => n.toLowerCase()));
  const vtypes = new Set(["purchase", "sales", "debit note", "credit note"]);
  return rows.map((row) => {
    const issues = [];
    if (!row.date || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) issues.push("Invalid date");
    if (!row.party_ledger) issues.push("Party ledger missing");
    else if (!autoCreate && !ldg.has(row.party_ledger.toLowerCase())) issues.push(`Unknown party '${row.party_ledger}'`);
    if (!row.item_name) issues.push("Item missing");
    else if (!autoCreate && !itm.has(row.item_name.toLowerCase())) issues.push(`Unknown item '${row.item_name}'`);
    if (!(row.qty > 0)) issues.push("Qty must be > 0");
    if (!(row.rate >= 0)) issues.push("Rate must be ≥ 0");
    if (row.discount_pct < 0 || row.discount_pct > 100) issues.push("Disc % must be 0-100");
    if (!vtypes.has(String(row.voucher_type).toLowerCase())) issues.push(`Bad voucher type '${row.voucher_type}'`);
    return { ...row, issues };
  });
}
